import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { useAvailabilityGuard } from '@/hooks/useAvailabilityGuard'
import { useGooglePlacesAutocomplete, type ParsedPlace } from '@/hooks/useGooglePlacesAutocomplete'
import {
  createContact,
  createContactAddress,
  saveCheckoutSession,
  syncCheckoutSession,
  updateContactAddress,
  updateContactProperties,
} from '@/lib/ecommerce/api'
import {
  checkoutStorage,
  consumeInternalCheckoutBack,
  isBackForwardNavigation,
  isInternalCheckoutBack,
} from '@/lib/ecommerce/checkoutStorage'
import {
  CheckoutPageShell,
  CheckoutFieldRow,
  CHECKOUT_INPUT_CLASS,
  CHECKOUT_SUBMIT_CLASS,
} from '@/components/ecommerce/CheckoutSteps'
import FlipButton from '@/components/ui/FlipButton'
import type { CheckoutBillingPagePayload } from '@/types/ecommerce'
import type { InsInputTelElement } from '@/types/insites-components'

/*
  Checkout Step 3 — Billing (Teamwork 26100597).

  Mirrors the ecommerce add-on's flow with MIG styling:
    1. "Same as shipping" checkbox (default ON) — when ON, the billing
       contact reuses the shipping contact details and the billing
       address is set equal to the shipping address (no new CRM address
       record is created — the session just carries
       billing_same_with_shipping=true and the shipping_* fields are
       copied into billing_* slots).
       When OFF, the user enters a separate billing contact + address.
    2. Search Address (Google Places Autocomplete, AU-restricted) +
       individual address fields (Address 1, Address 2, Suburb, State,
       Postcode, Country — defaults to Australia).
    3. Continue to Payment — saves billing fields onto the addon
       checkout session, then routes to /checkout/payment.

  Data persistence on Back: every keystroke writes a draft into
  sessionStorage; on mount, if a draft exists we restore it. Layered on
  top of the addon's server-side session that the Liquid wrapper
  pre-fills from.
*/

const DRAFT_KEY = 'mig-checkout-billing-draft'

interface BillingForm {
  same_as_shipping: boolean
  // Billing contact (only sent when same_as_shipping is false)
  bill_company_name: string
  bill_first_name: string
  bill_last_name: string
  bill_email: string
  bill_phone: string
  bill_phone_country_code: string
  // Address — same pattern as shipping. Empty selected_address_id means
  // the buyer is typing a new address; non-empty means they've picked
  // one of their saved CRM addresses from a card.
  selected_address_id: string
  address_1: string
  address_2: string
  suburb: string
  state: string
  postcode: string
  country: string
}

interface FieldErrors {
  bill_first_name?: string
  bill_last_name?: string
  bill_email?: string
  address_1?: string
  suburb?: string
  state?: string
  postcode?: string
  country?: string
}

function FieldError({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <p id={id} role="alert" className="mig-checkout-field-error">
      {children}
    </p>
  )
}

function SectionHeading({
  children,
  spacing = 8,
}: {
  children: React.ReactNode
  spacing?: 8 | 32 | 48
}) {
  return (
    <h2 className="mig-checkout-section-heading" data-spacing={spacing}>
      {children}
    </h2>
  )
}

/* Saved-address card. Same shape as the one in CheckoutShipping; kept
   inline here rather than extracted because the two pages otherwise
   have no shared component surface. Selected state shows a filled
   black circle with a white check on the left (matching the
   saved-card radio on /checkout/payment) plus a black 2px border. */
function AddressCard({
  primary,
  secondary,
  tertiary,
  label,
  selected,
  disabled,
  onSelect,
  hideIndicator,
}: {
  primary: string
  secondary: string
  tertiary?: string
  label?: string
  selected: boolean
  disabled?: boolean
  onSelect: () => void
  /** Skip rendering the radio-style indicator on the left. Used for
   *  the "+ Add new address" card which represents an action rather
   *  than a saved option. */
  hideIndicator?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className="mig-checkout-address-card"
      data-selected={selected}
    >
      {!hideIndicator && (
        <span className="mig-checkout-address-card-indicator" aria-hidden="true" />
      )}
      <span className="mig-checkout-address-card-content">
        {label && <span className="mig-checkout-address-card-label">{label}</span>}
        <span className="mig-checkout-address-card-primary">{primary}</span>
        {secondary && <span className="mig-checkout-address-card-line">{secondary}</span>}
        {tertiary && <span className="mig-checkout-address-card-line">{tertiary}</span>}
      </span>
    </button>
  )
}

export default function CheckoutBilling() {
  // Race-safe sold-item eviction.
  useAvailabilityGuard()
  const payload = usePageData<CheckoutBillingPagePayload>() ?? {
    is_authenticated: false,
    user_uuid: null,
    session_id: null,
    contact_email: null,
    contact_uuid: null,
    shipping_company_name: '',
    shipping_first_name: '',
    shipping_last_name: '',
    shipping_email: '',
    shipping_phone: '',
    shipping_phone_country_code: '+61',
    shipping_address_1: '',
    shipping_address_2: '',
    shipping_suburb: '',
    shipping_state: '',
    shipping_postcode: '',
    shipping_country: 'Australia',
    billing_same_with_shipping: true,
    billing_company_name: '',
    billing_first_name: '',
    billing_last_name: '',
    billing_email: '',
    billing_phone: '',
    billing_phone_country_code: '+61',
    billing_address_1: '',
    billing_address_2: '',
    billing_suburb: '',
    billing_state: '',
    billing_postcode: '',
    billing_country: 'Australia',
    saved_addresses: [],
    billing_address_id: null,
  }

  const savedAddresses = Array.isArray(payload.saved_addresses) ? payload.saved_addresses : []

  const initialForm: BillingForm = useMemo(() => {
    let selectedId = ''
    if (savedAddresses.length > 0) {
      const sessionId = payload.billing_address_id
      if (sessionId && savedAddresses.some((a) => a.id === sessionId)) {
        selectedId = sessionId
      } else {
        selectedId = savedAddresses[0].id
      }
    }
    return {
      same_as_shipping: payload.billing_same_with_shipping ?? true,
      bill_company_name: payload.billing_company_name ?? '',
      bill_first_name: payload.billing_first_name ?? '',
      bill_last_name: payload.billing_last_name ?? '',
      bill_email: payload.billing_email ?? '',
      bill_phone: payload.billing_phone ?? '',
      bill_phone_country_code: payload.billing_phone_country_code ?? '+61',
      selected_address_id: selectedId,
      address_1: payload.billing_address_1 ?? '',
      address_2: payload.billing_address_2 ?? '',
      suburb: payload.billing_suburb ?? '',
      state: payload.billing_state ?? '',
      postcode: payload.billing_postcode ?? '',
      country: payload.billing_country || 'Australia',
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [state, setState] = useState<BillingForm>(() => {
    if (typeof window === 'undefined') return initialForm

    // Restore the draft when either browser back/forward navigation
    // brought us here or the previous step's in-page BACK link set
    // the `mig-checkout-internal-back` flag. Other entries clear
    // the draft and fall through to `initialForm`. See
    // CheckoutContact / CheckoutShipping for the same logic.
    const cameFromInPageBack = isInternalCheckoutBack()
    if (cameFromInPageBack) consumeInternalCheckoutBack()
    if (!isBackForwardNavigation() && !cameFromInPageBack) {
      try {
        window.sessionStorage.removeItem(DRAFT_KEY)
      } catch {
        // sessionStorage may throw in private windows / quota — silent.
      }
      return initialForm
    }

    try {
      const raw = window.sessionStorage.getItem(DRAFT_KEY)
      if (!raw) return initialForm
      const parsed = JSON.parse(raw)
      const merged: BillingForm = { ...initialForm, ...parsed }
      // Self-heal: see CheckoutShipping for the full rationale. A
      // stale `selected_address_id` in the draft (left over from an
      // earlier render when cards were available) would otherwise
      // hide both the cards section and the new-address form, leaving
      // the Billing Address area blank.
      if (
        merged.selected_address_id &&
        !savedAddresses.some((a) => a.id === merged.selected_address_id)
      ) {
        merged.selected_address_id = ''
      }
      return merged
    } catch {
      return initialForm
    }
  })
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const telRef = useRef<InsInputTelElement | null>(null)

  // Persist draft on every change so navigating back/forward survives.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state))
    } catch {
      // sessionStorage can throw in private windows / quota — silent.
    }
  }, [state])

  function update<K extends keyof BillingForm>(key: K, value: BillingForm[K]) {
    setState((prev) => ({ ...prev, [key]: value }))
  }

  function onPlaceSelected(place: ParsedPlace) {
    setState((prev) => ({
      ...prev,
      address_1: place.address_1 || prev.address_1,
      address_2: place.address_2 || prev.address_2,
      suburb: place.suburb || prev.suburb,
      state: place.state || prev.state,
      postcode: place.postcode || prev.postcode,
      country: place.country || prev.country,
    }))
  }

  useGooglePlacesAutocomplete(searchInputRef, onPlaceSelected, {
    country: 'au',
    // Only run when the user is actually typing a fresh address — i.e.,
    // billing differs from shipping AND no saved card is selected.
    enabled: !state.same_as_shipping && !state.selected_address_id,
  })

  function validate(s: BillingForm): FieldErrors {
    const fe: FieldErrors = {}
    if (s.same_as_shipping) return fe
    if (!s.bill_first_name.trim()) fe.bill_first_name = 'First name is required.'
    if (!s.bill_last_name.trim()) fe.bill_last_name = 'Last name is required.'
    if (!s.bill_email.trim()) fe.bill_email = 'Email is required.'
    else if (!/^\S+@\S+\.\S+$/.test(s.bill_email.trim())) fe.bill_email = 'Please enter a valid email.'
    // Skip address validation if the buyer picked a saved card.
    if (!s.selected_address_id) {
      if (!s.address_1.trim()) fe.address_1 = 'Address Line 1 is required.'
      if (!s.suburb.trim()) fe.suburb = 'Suburb is required.'
      if (!s.state.trim()) fe.state = 'State is required.'
      if (!s.postcode.trim()) fe.postcode = 'Postcode is required.'
      if (!s.country.trim()) fe.country = 'Country is required.'
    }
    return fe
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    // Read the phone widget for billing contact (when not same-as-shipping).
    let phoneFromTel = state.bill_phone
    let codeFromTel = state.bill_phone_country_code
    if (!state.same_as_shipping && telRef.current?.getValues) {
      try {
        const vals = await telRef.current.getValues()
        phoneFromTel = vals.phone_number || phoneFromTel
        codeFromTel = vals.country_code ? `+${vals.country_code}` : codeFromTel
      } catch {
        // fall through to the raw state values
      }
    }

    const submittingState: BillingForm = {
      ...state,
      bill_phone: phoneFromTel,
      bill_phone_country_code: codeFromTel,
    }

    const fieldErrors = validate(submittingState)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) {
      setSubmitError(null)
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    try {
      if (submittingState.same_as_shipping) {
        // No new contact/address — the addon's save-checkout-session
        // (type=billing, billing_same_with_shipping=true) copies the
        // session_shipping_* values into the billing_* slots server-side.
        await saveCheckoutSession({
          type: 'billing',
          latest_step: 4,
          billing_same_with_shipping: 'true',
        })
        // GET-style sibling: copy shipping → billing in session so the
        // payment form's default_payload reads them correctly.
        const shipForSync = checkoutStorage.readShipping()
        if (shipForSync) {
          await syncCheckoutSession({
            billing_company_name: shipForSync.shipping_company_name,
            billing_contact_first_name: shipForSync.shipping_contact_first_name,
            billing_contact_last_name: shipForSync.shipping_contact_last_name,
            billing_contact_email: shipForSync.shipping_contact_email,
            billing_contact_phone_number: shipForSync.shipping_contact_phone_number,
            billing_contact_phone_country_code: shipForSync.shipping_contact_phone_country_code,
            billing_address_id: shipForSync.shipping_address_id,
            billing_address_1: shipForSync.shipping_address_1,
            billing_address_2: shipForSync.shipping_address_2,
            billing_suburb: shipForSync.shipping_suburb,
            billing_state: shipForSync.shipping_state,
            billing_postcode: shipForSync.shipping_postcode,
            billing_country: shipForSync.shipping_country,
          })
        }
        // Browser-side mirror: copy from shipping (since server
        // session writes don't persist on this instance). Re-use the
        // shipping address ID for billing so the payment form's
        // default_payload looks up the same crm_address record.
        const ship = checkoutStorage.readShipping()
        checkoutStorage.saveBilling({
          billing_same_with_shipping: true,
          billing_company_name: ship?.shipping_company_name ?? '',
          billing_contact_first_name: ship?.shipping_contact_first_name ?? '',
          billing_contact_last_name: ship?.shipping_contact_last_name ?? '',
          billing_contact_email: ship?.shipping_contact_email ?? '',
          billing_contact_phone_number: ship?.shipping_contact_phone_number ?? '',
          billing_contact_phone_country_code: ship?.shipping_contact_phone_country_code ?? '',
          billing_address_id: ship?.shipping_address_id ?? '',
          billing_address_1: ship?.shipping_address_1 ?? '',
          billing_address_2: ship?.shipping_address_2 ?? '',
          billing_address_3: ship?.shipping_address_3 ?? '',
          billing_suburb: ship?.shipping_suburb ?? '',
          billing_state: ship?.shipping_state ?? '',
          billing_postcode: ship?.shipping_postcode ?? '',
          billing_country: ship?.shipping_country ?? '',
        })
        if (ship?.shipping_address_id) {
          submittingState.selected_address_id = ship.shipping_address_id
        }
        // Mark the buyer's CRM contact so the payment form's
        // default_payload knows which address to use for billing.
        // We use the SAME crm_address record as shipping (since
        // "same as shipping" is selected), so we read the shipping
        // UUID we wrote in the previous step's contact update.
        const contactStored = checkoutStorage.readContact()
        const contactUuidForUpdate =
          payload.contact_uuid || contactStored?.resolved_contact_uuid || null
        // Get the shipping address UUID from the saved address list
        // or by looking it up via id.
        let billingAddressUuid: string | null = null
        if (ship?.shipping_address_id) {
          const matchedShip = savedAddresses.find((a) => a.id === ship.shipping_address_id)
          billingAddressUuid = matchedShip?.uuid ?? null
        }
        if (contactUuidForUpdate && billingAddressUuid) {
          try {
            await updateContactProperties({
              uuid: contactUuidForUpdate,
              checkout_billing_address_uuid: billingAddressUuid,
            })
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('updateContactProperties (billing same-as-ship) failed:', e)
          }
        }
      } else {
        // Different billing details. We need the buyer's main ORDER
        // contact UUID up-front because the new billing CRM address
        // record MUST be tied to it — not to a freshly-created
        // billing-only contact. The order callback
        // (callback_update_records.liquid) queries addresses by
        // related_uuid = order_contact_uuid + address_label="Billing".
        // Previously the billing address was tied to the new
        // billing-only contact, so the callback's lookup fell back to
        // the Shipping address and the order's billing fields ended
        // up identical to shipping even when the buyer entered a
        // different billing address (Teamwork 26214828).
        const contactStored = checkoutStorage.readContact()
        const orderContactUuid =
          payload.contact_uuid || contactStored?.resolved_contact_uuid || null
        if (!orderContactUuid) {
          throw new Error('Could not resolve the order contact. Please complete the contact step first.')
        }

        // Still create a CRM contact for the billing recipient (the
        // person on the invoice). This contact is independent of the
        // ORDER contact and may carry a different name/email/phone.
        // Its UUID isn't used directly here — the order's
        // billing_contact_* fields come from form.properties at submit
        // time — but we keep the CRM record so the support team has a
        // queryable "billing recipient" entry.
        const created = await createContact({
          first_name: submittingState.bill_first_name.trim(),
          last_name: submittingState.bill_last_name.trim(),
          email: submittingState.bill_email.trim(),
          mobile_phone_country_code: submittingState.bill_phone_country_code,
          mobile_phone_number: submittingState.bill_phone.trim(),
          company_name: submittingState.bill_company_name.trim(),
        })
        // billingContactUuid is no longer used as related_uuid for the
        // billing CRM address (we tie it to the order contact above so
        // the callback's address_label="Billing" lookup finds it). The
        // CRM contact itself still gets created above for record-keeping.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _billingContactUuid = created?.uuid ?? payload.contact_uuid ?? null

        let resolvedAddress = {
          address_1: submittingState.address_1.trim(),
          address_2: submittingState.address_2.trim(),
          suburb: submittingState.suburb.trim(),
          state: submittingState.state.trim(),
          postcode: submittingState.postcode.trim(),
          country: submittingState.country.trim(),
        }

        let chosenBillingAddressUuid: string | null = null
        if (submittingState.selected_address_id) {
          const picked = savedAddresses.find((a) => a.id === submittingState.selected_address_id)
          if (!picked) {
            throw new Error('Selected billing address is no longer available. Please pick another or add a new one.')
          }
          resolvedAddress = {
            address_1: (picked.address_1 ?? '').trim(),
            address_2: (picked.address_2 ?? '').trim(),
            suburb: (picked.suburb ?? '').trim(),
            state: (picked.state ?? '').trim(),
            postcode: (picked.postcode ?? '').trim(),
            country: (picked.country ?? '').trim(),
          }
          chosenBillingAddressUuid = picked.uuid ?? null
        } else if (payload.is_authenticated) {
          // Authenticated user new-address path — grows the buyer's
          // saved address book. Tie the new billing address to the
          // ORDER contact so the callback's address_label="Billing"
          // lookup finds it.
          const created = await createContactAddress({
            related_uuid: orderContactUuid,
            related_type: 'modules/insites_core/crm_contact',
            address_label: 'Billing',
            address_1: resolvedAddress.address_1,
            address_2: resolvedAddress.address_2,
            suburb: resolvedAddress.suburb,
            state: resolvedAddress.state,
            postcode: resolvedAddress.postcode,
            country: resolvedAddress.country,
            is_default_billing: true,
          })
          const newId = created?.id != null ? String(created.id) : ''
          if (newId) submittingState.selected_address_id = newId
          chosenBillingAddressUuid = (created?.uuid as string | undefined) ?? null
        } else {
          // GUEST new-address path: NO CRM call. Fields are written to
          // sessionStorage + session via the saveCheckoutSession/
          // syncCheckoutSession calls below. The single billing
          // crm_address record is created on /checkout/payment
          // immediately before order submit — see
          // finalizeGuestAddresses() in CheckoutPayment.tsx. This
          // guarantees exactly one billing record per guest per order.
        }

        // Persist the chosen billing address UUID on the buyer's
        // order contact so the payment form's default_payload resolves
        // it correctly.
        if (chosenBillingAddressUuid) {
          try {
            await updateContactProperties({
              uuid: orderContactUuid,
              checkout_billing_address_uuid: chosenBillingAddressUuid,
            })
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('updateContactProperties (separate billing) failed:', e)
          }
        }

        await saveCheckoutSession({
          type: 'billing',
          latest_step: 4,
          billing_same_with_shipping: 'false',
          billing_company_name: submittingState.bill_company_name.trim(),
          billing_contact_first_name: submittingState.bill_first_name.trim(),
          billing_contact_last_name: submittingState.bill_last_name.trim(),
          billing_contact_email: submittingState.bill_email.trim(),
          billing_contact_phone_number: submittingState.bill_phone.trim(),
          billing_contact_phone_country_code: submittingState.bill_phone_country_code,
          billing_address_1: resolvedAddress.address_1,
          billing_address_2: resolvedAddress.address_2,
          billing_suburb: resolvedAddress.suburb,
          billing_state: resolvedAddress.state,
          billing_postcode: resolvedAddress.postcode,
          billing_country: resolvedAddress.country,
          billing_address_id: submittingState.selected_address_id || '',
        })
        // GET-style sibling that persists session on this instance.
        await syncCheckoutSession({
          billing_company_name: submittingState.bill_company_name.trim(),
          billing_contact_first_name: submittingState.bill_first_name.trim(),
          billing_contact_last_name: submittingState.bill_last_name.trim(),
          billing_contact_email: submittingState.bill_email.trim(),
          billing_contact_phone_number: submittingState.bill_phone.trim(),
          billing_contact_phone_country_code: submittingState.bill_phone_country_code,
          billing_address_id: submittingState.selected_address_id || '',
          billing_address_1: resolvedAddress.address_1,
          billing_address_2: resolvedAddress.address_2,
          billing_suburb: resolvedAddress.suburb,
          billing_state: resolvedAddress.state,
          billing_postcode: resolvedAddress.postcode,
          billing_country: resolvedAddress.country,
        })
        // Authoritative browser-side mirror.
        checkoutStorage.saveBilling({
          billing_same_with_shipping: false,
          billing_company_name: submittingState.bill_company_name.trim(),
          billing_contact_first_name: submittingState.bill_first_name.trim(),
          billing_contact_last_name: submittingState.bill_last_name.trim(),
          billing_contact_email: submittingState.bill_email.trim(),
          billing_contact_phone_number: submittingState.bill_phone.trim(),
          billing_contact_phone_country_code: submittingState.bill_phone_country_code,
          billing_address_id: submittingState.selected_address_id || '',
          billing_address_1: resolvedAddress.address_1,
          billing_address_2: resolvedAddress.address_2,
          billing_address_3: '',
          billing_suburb: resolvedAddress.suburb,
          billing_state: resolvedAddress.state,
          billing_postcode: resolvedAddress.postcode,
          billing_country: resolvedAddress.country,
        })
      }

      // Overwrite the back-nav draft with the resolved submission
      // (not the pre-submit state). Same shape as CheckoutContact /
      // CheckoutShipping: `submittingState` carries the values that
      // actually went through validation including the resolved
      // bill_phone from telRef.current.getValues() and any
      // freshly-assigned selected_address_id. A future back-nav from
      // /checkout/payment restores exactly what was last submitted;
      // a fresh forward nav (Continue from /shipping, link click)
      // clears this draft in the state initializer before reading
      // the payload.
      try {
        window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(submittingState))
      } catch {
        // sessionStorage may throw in private windows / quota — silent.
      }

      // Pass through to the payment form via URL query params, since
      // server-side context.session writes don't persist on this
      // instance. The payment form's default_payload reads these and
      // uses them to look up the chosen crm_address records (and the
      // shipping_instructions text). All other order details — name,
      // email, phone, company — come from the user's CRM contact
      // record, which the React steps update via /api/contacts/put.
      const ship = checkoutStorage.readShipping()
      const bill = checkoutStorage.readBilling()
      const params = new URLSearchParams()
      if (ship?.shipping_address_id) {
        params.set('shipping_address_id', ship.shipping_address_id)
      }
      if (bill?.billing_address_id) {
        params.set('billing_address_id', bill.billing_address_id)
      }
      if (ship?.shipping_instructions) {
        params.set('shipping_instructions', ship.shipping_instructions)
      }
      const qs = params.toString()
      window.location.href = qs ? `/checkout/payment?${qs}` : '/checkout/payment'
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not save billing details.')
      setSubmitting(false)
    }
  }

  return (
    <CheckoutPageShell title="Checkout" active="billing" backHref="/checkout/shipping">
      <form onSubmit={handleSubmit} noValidate className="mig-form mig-checkout-form mig-checkout-form--billing">
        {/* Same-as-shipping toggle — gates the entire billing section */}
        <div className="mig-checkout-same-as-row mig-checkout-same-as-row--billing">
          <input
            id="mig-co-bill-same"
            type="checkbox"
            checked={state.same_as_shipping}
            onChange={(e) => update('same_as_shipping', e.target.checked)}
            disabled={submitting}
            className="mig-checkout-checkbox"
          />
          <label
            htmlFor="mig-co-bill-same"
            className="mig-co-field-label mig-checkout-checkbox-label"
          >
            My billing details are the same as my shipping details
          </label>
        </div>

        {/* Billing contact + address fields — only when checkbox is unchecked */}
        {!state.same_as_shipping && (
          <>
            <SectionHeading>Billing Contact</SectionHeading>
            <CheckoutFieldRow label="Company Name (optional)">
              <input
                type="text"
                autoComplete="organization"
                placeholder="Enter company name"
                value={state.bill_company_name}
                onChange={(e) => update('bill_company_name', e.target.value)}
                disabled={submitting}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            <CheckoutFieldRow label="First Name" required>
              <input
                type="text"
                autoComplete="given-name"
                placeholder="Enter first name"
                value={state.bill_first_name}
                onChange={(e) => update('bill_first_name', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.bill_first_name}
                aria-describedby={errors.bill_first_name ? 'mig-co-bill-fn-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.bill_first_name && <FieldError id="mig-co-bill-fn-err">{errors.bill_first_name}</FieldError>}

            <CheckoutFieldRow label="Last Name" required>
              <input
                type="text"
                autoComplete="family-name"
                placeholder="Enter last name"
                value={state.bill_last_name}
                onChange={(e) => update('bill_last_name', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.bill_last_name}
                aria-describedby={errors.bill_last_name ? 'mig-co-bill-ln-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.bill_last_name && <FieldError id="mig-co-bill-ln-err">{errors.bill_last_name}</FieldError>}

            <CheckoutFieldRow label="Email" required>
              <input
                type="email"
                autoComplete="email"
                placeholder="Enter email address"
                value={state.bill_email}
                onChange={(e) => update('bill_email', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.bill_email}
                aria-describedby={errors.bill_email ? 'mig-co-bill-em-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.bill_email && <FieldError id="mig-co-bill-em-err">{errors.bill_email}</FieldError>}

            <CheckoutFieldRow label="Phone">
              <ins-input-tel
                ref={telRef as React.Ref<InsInputTelElement>}
                country-code={state.bill_phone_country_code}
                phonenum-value={state.bill_phone}
                phonenum-placeholder="Enter phone number"
                no-areacode
              />
            </CheckoutFieldRow>

            <SectionHeading spacing={32}>Billing Address</SectionHeading>

            {savedAddresses.length > 0 && (
              <div className="mig-checkout-address-list">
                {savedAddresses.map((addr) => {
                  const line1 = [addr.address_1, addr.address_2].filter(Boolean).join(', ')
                  const locality = [addr.suburb, addr.state, addr.postcode]
                    .filter(Boolean)
                    .join(' ')
                    .trim()
                  return (
                    <AddressCard
                      key={addr.id}
                      label={addr.address_label || undefined}
                      primary={line1 || '(no street line on file)'}
                      secondary={locality}
                      tertiary={addr.country || undefined}
                      selected={state.selected_address_id === addr.id}
                      disabled={submitting}
                      onSelect={() => update('selected_address_id', addr.id)}
                    />
                  )
                })}
                <AddressCard
                  label="+ Add new address"
                  primary="Enter a different billing address"
                  secondary=""
                  selected={state.selected_address_id === ''}
                  disabled={submitting}
                  onSelect={() => update('selected_address_id', '')}
                  hideIndicator
                />
              </div>
            )}

            {!state.selected_address_id && (
              <>
                <CheckoutFieldRow label="Search Address">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Start typing your address…"
                    disabled={submitting}
                    autoComplete="off"
                    className={CHECKOUT_INPUT_CLASS}
                  />
                </CheckoutFieldRow>

                <CheckoutFieldRow label="Address Line 1" required>
              <input
                type="text"
                autoComplete="billing address-line1"
                placeholder="Street address"
                value={state.address_1}
                onChange={(e) => update('address_1', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.address_1}
                aria-describedby={errors.address_1 ? 'mig-co-bill-a1-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.address_1 && <FieldError id="mig-co-bill-a1-err">{errors.address_1}</FieldError>}

            <CheckoutFieldRow label="Address Line 2 (optional)">
              <input
                type="text"
                autoComplete="billing address-line2"
                placeholder="Apartment, suite, etc."
                value={state.address_2}
                onChange={(e) => update('address_2', e.target.value)}
                disabled={submitting}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>

            <CheckoutFieldRow label="Suburb" required>
              <input
                type="text"
                autoComplete="billing address-level2"
                placeholder="Suburb"
                value={state.suburb}
                onChange={(e) => update('suburb', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.suburb}
                aria-describedby={errors.suburb ? 'mig-co-bill-su-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.suburb && <FieldError id="mig-co-bill-su-err">{errors.suburb}</FieldError>}

            <CheckoutFieldRow label="State" required>
              <input
                type="text"
                autoComplete="billing address-level1"
                placeholder="State"
                value={state.state}
                onChange={(e) => update('state', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.state}
                aria-describedby={errors.state ? 'mig-co-bill-st-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.state && <FieldError id="mig-co-bill-st-err">{errors.state}</FieldError>}

            <CheckoutFieldRow label="Postcode" required>
              <input
                type="text"
                autoComplete="billing postal-code"
                inputMode="numeric"
                placeholder="Postcode"
                value={state.postcode}
                onChange={(e) => update('postcode', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.postcode}
                aria-describedby={errors.postcode ? 'mig-co-bill-pc-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
            {errors.postcode && <FieldError id="mig-co-bill-pc-err">{errors.postcode}</FieldError>}

            <CheckoutFieldRow label="Country" required>
              <input
                type="text"
                autoComplete="billing country-name"
                placeholder="Country"
                value={state.country}
                onChange={(e) => update('country', e.target.value)}
                disabled={submitting}
                aria-invalid={!!errors.country}
                aria-describedby={errors.country ? 'mig-co-bill-co-err' : undefined}
                className={CHECKOUT_INPUT_CLASS}
              />
            </CheckoutFieldRow>
                {errors.country && <FieldError id="mig-co-bill-co-err">{errors.country}</FieldError>}
              </>
            )}
          </>
        )}

        {submitError && (
          <p role="alert" className="mig-checkout-submit-error">{submitError}</p>
        )}

        <div className="mig-checkout-submit-row">
          <FlipButton
            type="submit"
            disabled={submitting}
            className={CHECKOUT_SUBMIT_CLASS}
            data-pending={submitting}
          >
            {submitting ? 'Saving…' : 'Continue to Payment'}
          </FlipButton>
        </div>
      </form>
    </CheckoutPageShell>
  )
}
